import { useCallback, useEffect, useState } from "react";
import type { MouseEvent } from "react";

import { analyticsInstance } from "@/libs/analytics";

/**
 * 프로모션 표면이 놓이는 자리. GA4 `creative_slot` 으로 나간다.
 *
 * - `top_strip` — GNB 아래 띠배너(GTA5 · 할로윈 등 같은 슬롯을 캠페인이 번갈아 쓴다)
 * - `home_popup` — 홈 우하단 팝업의 슬라이드(`FloatingPopup` 이 직접 기록한다)
 * - `tactsuit_hero` — `/tactsuit` 상단 히어로 이미지
 */
export type PromotionSlot = "top_strip" | "home_popup" | "tactsuit_hero";

export type PromotionInfo = {
  /** 로케일과 무관한 고정 키(예: `halloween-2026`). 번역된 문구를 넣지 않는다. */
  id: string;
  slot: PromotionSlot;
};

/**
 * 프로모션 표면 안의 클릭이 **링크(`<a>`) 클릭**인가 — `select_promotion` 을 셀지 정한다. 이미지·카피를 누른 것은
 * 세지 않는다(클릭률이 부풀지 않게). 훅과 홈 팝업 슬라이드(`floating-popup.tsx`)가 같은 판정을 쓴다.
 */
export function isPromotionLinkClick(target: EventTarget | null): boolean {
  return !!(target as Element | null)?.closest?.("a");
}

/**
 * 프로모션 표면의 노출·클릭을 기록한다 — 연말 세일 개발 목록 D1(2026-10-29).
 *
 * 종전엔 띠배너·팝업·히어로에 측정이 하나도 없어 「어느 문구가 팔았나」를 알 수 없었다.
 *
 * - **노출** = 요소가 절반 이상 보였을 때 **마운트당 1회**(`HapticSummaryCard` 와 같은 기준).
 *   같은 자리의 캠페인이 바뀌면(`id`) 다시 센다.
 * - **클릭** = 요소 안의 링크(`<a>`)를 눌렀을 때. 링크 없는 표면(예: 할로윈 기간 히어로)은
 *   노출만 남는다.
 *
 * 돌려준 `attach`(→ `ref` 속성)·`onClickCapture` 를 **표면의 기존 루트**에 붙인다 — 감싸는 요소를 더하지 않아
 * 레이아웃이 바뀌지 않는다. `promotion` 이 `null` 이면(표면을 안 그리는 렌더) 아무것도 안 보낸다.
 *
 * ⚠️ **호출처에서 구조 분해해 쓴다** — `const { attach, onClickCapture } = usePromotionTracking(…)`.
 * 반환 객체를 통째로 받아 `promo.attach` 를 `ref` 에 넘기면 React 컴파일러 린트(`react-hooks/refs`)가 그
 * 객체를 ref 로 보고 `promo.onClickCapture` 까지 「렌더 중 ref 읽기」로 막는다(2026-10-29 실측 7건 —
 * `useRef` 를 콜백 ref 로 바꾸고 이름을 `ref`→`attach` 로 바꿔도 그대로였고, 구조 분해로 0건).
 */
export function usePromotionTracking(promotion: PromotionInfo | null) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  // `HTMLElement` 를 받는 콜백이라 `<a>`(Link)·`<div>` 어느 루트에도 붙는다.
  const attach = useCallback((node: HTMLElement | null) => setEl(node), []);
  const id = promotion?.id ?? null;
  const slot = promotion?.slot ?? null;

  useEffect(() => {
    if (!el || !id || !slot || typeof IntersectionObserver === "undefined") {
      return;
    }
    let sent = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !sent) {
          sent = true;
          analyticsInstance?.logViewPromotion({ id, slot });
          io.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [el, id, slot]);

  const onClickCapture = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (!id || !slot) return;
      if (!isPromotionLinkClick(e.target)) return;
      analyticsInstance?.logSelectPromotion({ id, slot });
    },
    [id, slot],
  );

  return { attach, onClickCapture };
}
